<template>
  <div class="login-page">
    <!-- 初始加载动画 -->
    <div v-if="showInitialLoading" class="initial-loading-overlay">
      <div class="initial-loading-content">
        <img src="@/assets/jfLogin.png" alt="江丰智造" class="loading-logo">
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressWidth + '%' }"></div>
          </div>
          <!-- <div class="progress-text">{{ Math.round(progressWidth) }}%</div> -->
        </div>
      </div>
    </div>

    <v-card
      v-show="!showInitialLoading"
      class="login-card"
      elevation="3"
    >
      <v-card-title class="text-center logo-container">
        <img src="@/assets/jfLogin.png" alt="江丰智造" class="logo-img">
      </v-card-title>

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        closable
        class="mb-4"
        @click:close="error = null"
      >
        {{ error }}
      </v-alert>

      <v-form @submit.prevent="handleLogin">
        <v-text-field
          v-model="phone"
          label="手机号"
          variant="outlined"
          :rules="[v => !!v || '请输入手机号', v => /^1[3-9]\d{9}$/.test(v) || '请输入正确的手机号']"
          class="mb-3"
          @keyup.enter="handleLogin"
        />

        <div class="d-flex gap-2 mb-4">
          <v-text-field
            v-model="code"
            label="验证码"
            variant="outlined"
            class="input-info"
            :rules="[v => !!v || '请输入验证码']"
            @keyup.enter="handleLogin"
          />
          
          <v-btn
            :disabled="!!countdown || sendingCode"
            :loading="sendingCode"
            :class="['login-btn', 'code-btn']"
            min-width="120"
            @click="handleSendCode"
          >
          <!--  || !phone || !/^1[3-9]\d{9}$/.test(phone) -->
            {{ countdown ? `${countdown}秒后重试` : '获取验证码' }}
          </v-btn>
        </div>

        <v-btn
          block
          :class="['login-btn']"
          size="large"
          :loading="loggingIn" 
          type="submit"
        >
        <!--   :disabled="!phone || !code || !/^1[3-9]\d{9}$/.test(phone)" -->
          登录
        </v-btn>
      </v-form>
    </v-card>
    <div class="beian-info">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">沪ICP备2024078471号-2</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { authApi } from '@/services/api'
import { getNavMenus } from '@/router'
import { updateVersionCache, getCurrentVersion, getCurrentBuildTime } from '@/utils/versionUtils'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const phone = ref('')
const code = ref('')
const error = ref<string | null>(null)
const sendingCode = ref(false)  // 发送验证码的loading状态
const loggingIn = ref(false)    // 登录的loading状态
const countdown = ref(0)
const showInitialLoading = ref(true)
const progressWidth = ref(0)

// 倒计时
function startCountdown() {
  countdown.value = 60
  const timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}

// 发送验证码
async function handleSendCode() {
  if (sendingCode.value || !phone.value || !/^1[3-9]\d{9}$/.test(phone.value)) {
    return
  }

  try {
    sendingCode.value = true
    const res = await authApi.sendSmsCode(phone.value)
    if (res.code === 0) {
      startCountdown()
      error.value = null
    } else {
      error.value = res.msg || '发送验证码失败'
    }
  } catch (err: any) {
    error.value = err.message
  } finally {
    sendingCode.value = false
  }
}

// 登录成功后重新获取版本信息
async function updateVersionInfoAfterLogin() {
  try {
    // 获取当前版本信息
    const currentVersion = getCurrentVersion()
    const buildTime = getCurrentBuildTime()
    
    console.log('登录后获取版本信息:', { 
      currentVersion, 
      buildTime,
      timestamp: new Date().toISOString()
    })
    
    // 更新版本缓存，确保版本信息是最新的
    // updateVersionCache()
    // console.log('版本缓存已更新')
    
    // 可以在这里添加其他版本相关的逻辑
    // 比如检查是否需要显示版本更新提示等
    
  } catch (versionError) {
    console.warn('更新版本信息失败:', versionError)
    // 版本信息更新失败不影响登录流程
  }
}

// 登录
async function handleLogin() {
  if (loggingIn.value || !phone.value || !code.value) {
    return
  }

  try {
    loggingIn.value = true
    const res = await authApi.login({
      mobilePhone: phone.value,
      code: code.value
    })

    console.log('登录响应:', res)

    if (res.code === 0 && res.data.user.auths !== 'jf_business') {
      // 保存用户信息
      localStorage.setItem('token', res.data.token)
      userStore.setToken(res.data.token)
      userStore.setUserInfo(res.data.user)

      // 登录成功后重新获取版本信息
      await updateVersionInfoAfterLogin()

      // 登录成功后跳转
      const redirect = route.query.redirect as string
      const menus = getNavMenus()
      console.log('menus:', menus)
      for(let i = 0; i < menus.length; i++){
        if(menus[i]?.title === '业务'){
          await router.replace(redirect || '/contract')
          break
        }else if(menus[i]?.title === '物控'){
          await router.replace(redirect || '/material-control')
          break
        }else if(menus[i]?.title === '成本'){
          await router.replace(redirect || '/estimate')
          break
        }else if(menus[i]?.title === '设计'){
          await router.replace(redirect || '/design-draft')
          break
        }else if(menus[i]?.title === '采购'){
          await router.replace(redirect || '/purchase')
          break
        }else if(menus[i]?.title === '生产'){
          await router.replace(redirect || '/factory')
          break
        }else if(menus[i]?.title === '财务'){
          await router.replace(redirect || '/finance-audit')
          break
        }else if(menus[i]?.title === '集团'){
          await router.replace(redirect || '/group-audit')
          break
        }else if(menus[i]?.title === '公告'){
          await router.replace(redirect || '/notice')
          break
        } else {  
          await router.replace(redirect || '/staff')
          break
        }
      }
      // console.log('准备跳转到:', redirect || '/search')
      // await router.replace(redirect || '/search')
      // console.log('跳转完成')
    } else if (res.code === 0 && res.data.user.auths === 'jf_business') {
      error.value = '暂无权限，请联系管理员添加权限'
    } else {
      error.value = res.message
    }
  } catch (err: any) {
    error.value = err.message
  } finally {
    loggingIn.value = false
  }
}

// 初始加载动画
function startInitialLoading() {
  const loadingDuration = 3000 // 3秒
  let startTime = 0

  function updateProgress() {
    const currentTime = Date.now()
    const elapsedTime = currentTime - startTime
    const progress = (elapsedTime / loadingDuration) * 100
    progressWidth.value = progress

    if (progress < 100) {
      requestAnimationFrame(updateProgress)
    } else {
      showInitialLoading.value = false
    }
  }

  startTime = Date.now()
  requestAnimationFrame(updateProgress)
}

// 在组件挂载后启动初始加载动画
onMounted(() => {
  startInitialLoading()
})
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  background-image: url('@/assets/bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.login-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: rgba(10, 25, 47, 0.56);
  z-index: 0;
}

.login-card {
  padding: 32px;
  width: 100%;
  max-width: 400px;
  border-radius: 8px;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(20px);
  background-color: rgba(13, 23, 42, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.37);
  border: 1px solid rgba(64, 169, 255, 0.1);
  color: white;

  .logo-container {
    padding: 0;
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .logo-img {
    max-width: 180px;
    height: auto;
    width: 68px;
  }

  .site-name {
    font-size: 16px;
    font-weight: 300;
    color: white;
  }

  :deep(.v-field__input),
  :deep(.v-label),
  :deep(.v-card-title) {
    color: white;
  }

  :deep(.v-field) {
    --v-field-border-color: rgba(255, 255, 255, 0.3);
    --v-field-border-opacity: 1;
  }
}

.gap-2 {
  gap: 8px;
}

.login-btn {
  background-color: #D2475D !important;
  color: white !important;
}

.code-btn{
  height: 50px !important;
}

// 设置手机号和验证码输入框的高度为50px
:deep(.v-text-field) {
  .v-field__input {
    min-height: 50px !important;
  }
  
  .v-field {
    min-height: 50px !important;
  }
}

// 更精确的设置输入框高度
:deep(.v-field) {
  min-height: 50px !important;
  height: 50px !important;
}

:deep(.v-field__input) {
  min-height: 50px !important;
  height: 50px !important;
}

:deep(.v-text-field .v-field) {
  min-height: 50px !important;
  height: 50px !important;
}

:deep(.v-text-field .v-field__input) {
  min-height: 50px !important;
  height: 50px !important;
}

:deep(.mb-3) {
  margin-bottom: 12px !important;
}

.beian-info {
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 1;

  a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    font-size: 14px;
    
    &:hover {
      color: white;
    }
  }
}

.initial-loading-overlay {
  position: fixed;
  inset: 0;
  background-image: url('@/assets/bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(10, 25, 47, 0.8);
    z-index: 0;
  }

  .initial-loading-content {
    position: relative;
    z-index: 1;
    // background-color: rgba(255, 255, 255, 0.95);
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    text-align: center;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);

    .loading-logo {
      max-width: 68px;
      height: auto;
      margin-bottom: 30px;
      filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
    }

    .progress-container {
      width: 98px;
      margin: 0 auto;

      .progress-bar {
        width: 100%;
        height: 8px;
        background-color: rgba(210, 71, 93, 0.2);
        border-radius: 4px;
        overflow: hidden;
        position: relative;

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #D2475D, #ff6b7a);
          border-radius: 4px;
          transition: width 0.1s ease;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            animation: shimmer 1.5s infinite;
          }
        }
      }

      .progress-text {
        margin-top: 12px;
        font-weight: 600;
        color: #333;
        font-size: 14px;
      }
    }
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>
